import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';

const CustomEventRegistration = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Determine active tab based on path
    const isWorkshop = location.pathname.includes('workshop');

    const [config, setConfig] = useState(null);
    const [configLoading, setConfigLoading] = useState(true);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        contactNumber: '',
        registrationCategory: '',
        ...{} // Dynamic fields will go here
    });

    // We'll store addon selections here: { addonId: boolean }
    const [selectedAddons, setSelectedAddons] = useState({});
    const [showSpouseNotice, setShowSpouseNotice] = useState(false);

    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [hodCertFile, setHodCertFile] = useState(null);

    // Manual Payment States
    const [transactionReceiptFile, setTransactionReceiptFile] = useState(null);
    const [transactionReceiptPreview, setTransactionReceiptPreview] = useState(null);
    const transactionReceiptRef = useRef();

    const [submitted, setSubmitted] = useState(false);
    const [ticketData, setTicketData] = useState(null);
    const [focused, setFocused] = useState('');
    const [loading, setLoading] = useState(false);

    const photoRef = useRef();
    const hodRef = useRef();

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/config`);
                const data = await res.json();
                if (data.success && data.data) {
                    setConfig(data.data);

                    // Initialize dynamic form fields
                    const initialData = {
                        fullName: '',
                        email: '',
                        contactNumber: '',
                        registrationCategory: '',
                    };
                    if (data.data.formFields) {
                        data.data.formFields.forEach(f => {
                            initialData[f.name] = '';
                        });
                    }
                    setFormData(initialData);
                }
            } catch (err) {
                console.error("Failed to fetch config", err);
            } finally {
                setConfigLoading(false);
            }
        };
        fetchConfig();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => {
            const updated = { ...prev, [name]: value };
            if (name === 'registrationCategory') {
                // Clear selected addons when category changes to re-evaluate exclusions
                setSelectedAddons({});
            }
            return updated;
        });
    };

    const handleAddonChange = (addonId, checked) => {
        setSelectedAddons(prev => ({ ...prev, [addonId]: checked }));
        if (checked) {
            const addon = config?.ticketConfig?.addons?.find(a => a.id === addonId);
            if (addon?.label?.toLowerCase().includes('spouse') || addon?.label?.toLowerCase().includes('accompanying')) {
                setShowSpouseNotice(true);
            }
        }
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setPhotoFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setPhotoPreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const handleTransactionReceiptChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.size > 5 * 1024 * 1024) {
                alert("File size must be less than 5MB");
                return;
            }
            setTransactionReceiptFile(file);
            setTransactionReceiptPreview(URL.createObjectURL(file));
        }
    };

    const handleHodChange = (e) => {
        const file = e.target.files[0];
        if (file) setHodCertFile(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // 1. Basic Static Validations
        if (!formData.fullName || !formData.email || !formData.contactNumber || !formData.registrationCategory) {
            alert("Please fill all required base fields (Name, Email, Contact, Category).");
            return;
        }

        // 2. Dynamic Field Validations (respects category-specific rule overrides)
        if (config?.formFields) {
            for (const field of config.formFields) {
                let isFieldRequired = field.required;
                let isFieldHidden = false;

                if (formData.registrationCategory && field.categoryRules) {
                    const rule = field.categoryRules[formData.registrationCategory];
                    if (rule === 'mandatory') isFieldRequired = true;
                    else if (rule === 'optional') isFieldRequired = false;
                    else if (rule === 'hidden') isFieldHidden = true;
                }

                if (!isFieldHidden && isFieldRequired && !formData[field.name]) {
                    alert(`Please fill the required field: ${field.label}`);
                    return;
                }
            }
        }

        // 3. Photo & Cert Validations
        if (!photoFile) {
            alert('Please upload your passport-size photo before submitting.');
            return;
        }

        const tConf = config?.ticketConfig;
        const category = tConf?.categories?.find(c => c.id === formData.registrationCategory);

        if (category?.requiresHodCert && !hodCertFile) {
            alert('Please upload the HOD Attested Certificate before submitting.');
            return;
        }

        // Payment validations (only when a fee is due)
        if (dPrice.grandTotal > 0) {
            if (!formData.transactionId || !formData.transactionId.trim()) {
                alert('Please enter your Transaction ID / UTR No. before submitting.');
                return;
            }
            if (!transactionReceiptFile) {
                alert('Please upload your payment screenshot / proof before submitting.');
                return;
            }
        }

        setLoading(true);
        try {
            const submissionData = { ...formData };
            // Add on selections mapping to names for tracking
            if (config?.ticketConfig?.addons) {
                config.ticketConfig.addons.forEach(addon => {
                    submissionData[`addon_${addon.name}`] = !!selectedAddons[addon.id] ? 'Yes' : 'No';
                });
            }

            // 4. Calculate final amount dynamically
            let finalAmount = 0;
            let isForeign = !!category?.isForeign;

            let activeDr = null;
            const now = moment();
            for (const dr of (tConf?.dateRanges || [])) {
                if (!dr.deadline) continue;
                if (now.isSameOrBefore(moment(dr.deadline).endOf('day'))) {
                    activeDr = dr;
                    break;
                }
            }
            if (!activeDr && tConf?.dateRanges?.length > 0) {
                activeDr = tConf.dateRanges.find(dr => dr.isSpot) || tConf.dateRanges[tConf.dateRanges.length - 1];
            }

            if (tConf && formData.registrationCategory && activeDr) {
                // Record the detected date configuration for reference
                submissionData.registrationDate = activeDr.label;

                // Base price from matrix
                const matrixPrice = tConf.pricingMatrix?.[formData.registrationCategory]?.[activeDr.id];
                if (matrixPrice) {
                    finalAmount += parseInt(matrixPrice, 10);
                }

                // Add-ons
                if (tConf.addons) {
                    tConf.addons.forEach(addon => {
                        // Check if selected and not excluded
                        if (selectedAddons[addon.id]) {
                            const isExcluded = addon.excludedCategories?.includes(formData.registrationCategory);
                            if (!isExcluded) {
                                finalAmount += parseInt(addon.price || 0, 10);
                            }
                        }
                    });
                }

                // Overwrite the category ID with the actual category name for backend storage
                submissionData.registrationCategory = category.name;
                submissionData.categoryId = category.id;

                // Store complete category metadata according to request
                if (category.requiresIdNumber) {
                    submissionData.idNumberLabel = category.idNumberLabel || 'ID Number';
                }
                submissionData.requiresHodCert = category.requiresHodCert || false;
                submissionData.isForeign = category.isForeign || false;
            }

            // Step 1: Manual Form Submission
            const formDataToSend = new FormData();
            formDataToSend.append('data', JSON.stringify(submissionData));
            formDataToSend.append('paymentAmount', finalAmount);
            formDataToSend.append('transactionId', formData.transactionId || '');

            if (photoFile) formDataToSend.append('photo', photoFile);
            if (hodCertFile) formDataToSend.append('hodCert', hodCertFile);
            if (transactionReceiptFile) formDataToSend.append('transactionReceipt', transactionReceiptFile);

            const regRes = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/register`, {
                method: 'POST',
                body: formDataToSend
            });
            const regData = await regRes.json();

            if (regData.success) {
                setSubmitted(true);
                // Redirect user directly to their public M-ticket
                window.location.href = `/event/jss/conference/participant/${regData.data.registrationId}`;
            } else {
                alert(regData.message || 'Registration failed');
            }

        } catch (error) {
            console.error(error);
            alert('An error occurred during submission');
        } finally {
            setLoading(false);
        }
    };

    // --- Pricing Calculation for Display --- //
    const calculateDisplayPrices = () => {
        if (!config || !config.ticketConfig) return { basePrice: 0, addonTotal: 0, grandTotal: 0, isForeign: false, activeDr: null };

        const tConf = config.ticketConfig;
        const cat = tConf.categories.find(c => c.id === formData.registrationCategory);
        const isForeign = cat ? !!cat.isForeign : false;

        let basePrice = 0;
        let addonTotal = 0;

        let activeDr = null;
        const now = moment();
        for (const dr of (tConf.dateRanges || [])) {
            if (!dr.deadline) continue;
            if (now.isSameOrBefore(moment(dr.deadline).endOf('day'))) {
                activeDr = dr;
                break;
            }
        }
        if (!activeDr && tConf.dateRanges?.length > 0) {
            activeDr = tConf.dateRanges.find(dr => dr.isSpot) || tConf.dateRanges[tConf.dateRanges.length - 1];
        }

        if (cat && activeDr) {
            const p = tConf.pricingMatrix?.[cat.id]?.[activeDr.id];
            if (p) basePrice = parseInt(p, 10);
        }

        if (cat && tConf.addons) {
            tConf.addons.forEach(addon => {
                if (selectedAddons[addon.id] && !addon.excludedCategories?.includes(cat.id)) {
                    addonTotal += parseInt(addon.price || 0, 10);
                }
            });
        }

        return {
            basePrice,
            addonTotal,
            grandTotal: basePrice + addonTotal,
            isForeign,
            activeDr
        };
    };

    const dPrice = calculateDisplayPrices();
    const computedPriceStr = dPrice.grandTotal > 0
        ? (dPrice.isForeign ? `$${dPrice.grandTotal}` : `₹${dPrice.grandTotal.toLocaleString('en-IN')}`)
        : null;

    if (configLoading) {
        return (
            <div className="min-h-screen flex text-[#5928ed] items-center justify-center bg-[#fcfaff]">
                <p className="font-semibold text-xl">Loading configuration...</p>
            </div>
        );
    }

    const inputBase = `w-full bg-white border-2 rounded-xl px-4 py-3 text-gray-800 text-sm font-medium transition-all duration-200 outline-none placeholder-gray-300`;
    const inputFocusCls = `border-[#5928ed] ring-4 ring-[#5928ed]/10`;
    const inputDefaultCls = `border-gray-200 hover:border-gray-300`;
    const getInputClass = (name) =>
        `${inputBase} ${focused === name ? inputFocusCls : inputDefaultCls}`;
    const getSelectClass = (name) =>
        `${inputBase} cursor-pointer appearance-none pr-10 ${focused === name ? inputFocusCls : inputDefaultCls}`;

    return (
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif" }} className="min-h-screen bg-gradient-to-br from-[#fcfaff] via-[#fef9f5] to-[#f5ede4]">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        .sans { font-family: 'DM Sans', sans-serif; }
        .pill-option input[type="radio"] { display: none; }
        .pill-option label {
          display: inline-flex; align-items: center; cursor: pointer;
          padding: 8px 18px; border-radius: 999px; border: 2px solid #e5e7eb;
          font-size: 13px; font-weight: 500; color: #6b7280;
          transition: all 0.2s; background: white;
          font-family: 'DM Sans', sans-serif;
        }
        .pill-option input[type="radio"]:checked + label {
          background: #5928ed; border-color: #5928ed; color: white;
          box-shadow: 0 4px 12px rgba(181,69,27,0.25);
        }
        .pill-option label:hover { border-color: #5928ed; color: #5928ed; }
        .pill-option input[type="radio"]:checked + label:hover { color: white; }
        .submit-btn {
          background: linear-gradient(135deg, #5928ed, #7244f7);
          transition: all 0.3s;
        }
        .submit-btn:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(181,69,27,0.4); }
        .submit-btn:active { transform: translateY(0); }
        .contact-chip {
          display: inline-flex; align-items: center; gap: 6px;
          background: white; border: 1.5px solid #f0e0d6;
          border-radius: 999px; padding: 6px 14px;
          font-size: 12.5px; color: #6b4c3b;
          font-family: 'DM Sans', sans-serif; font-weight: 500;
        }
        .fade-in { animation: fadeIn 0.35s ease; }
        @keyframes fadeIn { from { opacity:0; transform: translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        .section-divider { height: 1px; background: linear-gradient(to right, transparent, #f0e0d6, transparent); }
        .price-badge {
          display: inline-flex; align-items: center; gap: 6px;
          background: linear-gradient(135deg, #5928ed, #7244f7);
          color: white; border-radius: 12px; padding: 10px 20px;
          font-family: 'DM Sans', sans-serif; font-weight: 700;
          font-size: 16px; box-shadow: 0 4px 14px rgba(181,69,27,0.3);
          animation: fadeIn 0.3s ease;
        }
        .select-wrapper { position: relative; }
        .select-wrapper::after {
          content: '▾'; position: absolute; right: 14px; top: 50%;
          transform: translateY(-50%); color: #9ca3af; pointer-events: none;
          font-size: 14px;
        }
        .upload-zone {
          border: 2px dashed #e5e7eb; border-radius: 16px;
          transition: all 0.2s; cursor: pointer; background: white;
        }
        .upload-zone:hover { border-color: #5928ed; background: #fcfaff; }
        .upload-zone-active { border-color: #5928ed; background: #fcfaff; }
        .disclaimer-box {
          background: linear-gradient(135deg, #fffbeb, #fef3c7);
          border: 1.5px solid #fcd34d; border-radius: 16px;
        }
        .hod-box {
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 1.5px solid #93c5fd; border-radius: 16px;
        }
      `}</style>

            <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">

                {/* ── Header ── */}
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-6">
                    <div style={{ background: 'linear-gradient(135deg, #4013b5 0%, #5928ed 50%, #7244f7 100%)' }}
                        className="px-6 py-8 sm:px-10 sm:py-10 text-white text-center">
                        <div className="sans text-xs tracking-[0.2em]  opacity-80 mb-2 font-semibold">52nd ANNUAL STATE CONFERENCE</div>
                        <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-1">KAPCON 2026</h1>
                        <div className="section-divider my-4 opacity-30" />
                        <p className="sans text-sm sm:text-base opacity-90 leading-relaxed">
                            DEPARTMENT OF PATHOLOGY, JSS MEDICAL COLLEGE<br />JSS AHER, MYSURU
                        </p>
                        <p className="sans text-xs opacity-70 mt-2">in association with</p>
                        <p className="sans text-xs font-semibold opacity-90 mt-1">KARNATAKA CHAPTER OF IAPM (KCIAPM)</p>
                    </div>
                    <div className="px-6 py-5 sm:px-10 flex flex-col sm:flex-row gap-3 justify-center items-center bg-[#fcfaff]">
                        <div className="flex items-center gap-2 sans text-sm text-gray-600">
                            <span className="text-[#5928ed]">📍</span><span>JSS Medical College, Mysuru</span>
                        </div>
                        <div className="hidden sm:block w-1 h-1 rounded-full bg-gray-300" />
                        <div className="flex items-center gap-2 sans text-sm text-gray-600">
                            <span className="text-[#5928ed]">📅</span><span>3rd – 5th July 2026</span>
                        </div>
                    </div>
                </div>

                {/* ── Contact ── */}
                <div className="bg-white rounded-2xl shadow-md px-6 py-5 sm:px-8 mb-6">
                    <p className="sans text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3">Contact Us</p>
                    <div className="flex flex-wrap gap-2">
                        {[
                            { icon: '📞', text: 'Dr Deepika G · 9739320067' },
                            { icon: '📞', text: 'Dr Pallavi B R · 9632744696' },
                            { icon: '✉️', text: 'kapcon2026@gmail.com' },
                        ].map((c, i) => (
                            <span key={i} className="contact-chip"><span>{c.icon}</span>{c.text}</span>
                        ))}
                    </div>
                </div>

                {/* ── Navigation Tabs ── */}
                <div className="flex bg-white rounded-xl shadow-md p-1 mb-6 border border-gray-100">
                    <button
                        onClick={() => navigate('/event/jss/conference')}
                        className={`flex-1 py-3 text-sm font-bold rounded-lg transition-all ${!isWorkshop ? 'bg-[#5928ed] text-white shadow' : 'text-gray-500 hover:bg-gray-50'}`}
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                        Registration for Conference
                    </button>
                    <button
                        onClick={() => navigate('/event/jss/workshop')}
                        className={`flex-1 py-3 text-sm font-bold rounded-lg transition-all ${isWorkshop ? 'bg-[#5928ed] text-white shadow' : 'text-gray-500 hover:bg-gray-50'}`}
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                        Registration for Workshop
                    </button>
                </div>

                {/* ── Form ── */}
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                    <div className="px-6 py-5 sm:px-10 border-b border-gray-100 flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Registration Form</h2>
                            <p className="sans text-xs text-gray-400 mt-0.5">All fields marked * are required</p>
                        </div>
                        <div className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ background: 'linear-gradient(135deg, #5928ed, #7244f7)' }}>
                            <span className="text-white text-lg">📋</span>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="px-6 py-7 sm:px-10 space-y-5">

                        {/* ── Personal Info ── */}
                        <p className="sans text-[11px] font-bold tracking-widest uppercase text-[#5928ed]">Personal Information</p>


                        <div>
                            <label className="sans block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Full Name *</label>
                            <input type="text" name="fullName" value={formData.fullName} required placeholder="Dr. Jane Smith"
                                onChange={handleChange} onFocus={() => setFocused('fullName')} onBlur={() => setFocused('')}
                                className={getInputClass('fullName')} style={{ fontFamily: "'DM Sans', sans-serif" }} />
                        </div>

                        <div>
                            <label className="sans block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Email Address *</label>
                            <input type="email" name="email" value={formData.email} required placeholder="you@example.com"
                                onChange={handleChange} onFocus={() => setFocused('email')} onBlur={() => setFocused('')}
                                className={getInputClass('email')} style={{ fontFamily: "'DM Sans', sans-serif" }} />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="sans block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Contact Number *</label>
                                <input type="tel" name="contactNumber" value={formData.contactNumber} required placeholder="+91 98765 43210"
                                    onChange={handleChange} onFocus={() => setFocused('contactNumber')} onBlur={() => setFocused('')}
                                    className={getInputClass('contactNumber')} style={{ fontFamily: "'DM Sans', sans-serif" }} />
                            </div>
                            <div>
                                <label className="sans block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">WhatsApp Number *</label>
                                <input type="tel" name="whatsappNumber" value={formData.whatsappNumber} required placeholder="+91 98765 43210"
                                    onChange={handleChange} onFocus={() => setFocused('whatsappNumber')} onBlur={() => setFocused('')}
                                    className={getInputClass('whatsappNumber')} style={{ fontFamily: "'DM Sans', sans-serif" }} />
                            </div>
                        </div>

                        {/* Dynamic Render of Additional Information from formFields */}
                        {config?.formFields?.length > 0 && (
                            <>
                                <div className="section-divider" />
                                <p className="sans text-[11px] font-bold tracking-widest uppercase text-[#5928ed]">Additional Information</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {config.formFields.map(field => {
                                        let isRequired = field.required;
                                        let isHidden = false;

                                        if (formData.registrationCategory && field.categoryRules) {
                                            const rule = field.categoryRules[formData.registrationCategory];
                                            if (rule === 'mandatory') isRequired = true;
                                            else if (rule === 'optional') isRequired = false;
                                            else if (rule === 'hidden') isHidden = true;
                                        }

                                        if (isHidden) return null;

                                        const colSpan = field.halfWidth ? "col-span-1" : "sm:col-span-2 col-span-1";

                                        return (
                                            <div key={field.id} className={colSpan}>
                                                <div className="flex items-end justify-between mb-1.5">
                                                    <label className="sans block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                        {field.label} {isRequired && '*'}
                                                    </label>
                                                    {field.isGovId && field.forgotIdUrl && (
                                                        <a href={field.forgotIdUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline">
                                                            Forgot ID?
                                                        </a>
                                                    )}
                                                </div>

                                                {(field.type === 'text' || field.type === 'tel' || field.type === 'email') && (
                                                    <input
                                                        type={field.type}
                                                        name={field.name}
                                                        value={formData[field.name] || ''}
                                                        required={isRequired}
                                                        placeholder={`Enter ${field.label}`}
                                                        onChange={handleChange}
                                                        onFocus={() => setFocused(field.name)}
                                                        onBlur={() => setFocused('')}
                                                        className={getInputClass(field.name)}
                                                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                                                    />
                                                )}

                                                {field.type === 'textarea' && (
                                                    <textarea
                                                        name={field.name}
                                                        value={formData[field.name] || ''}
                                                        required={isRequired}
                                                        placeholder={`Enter ${field.label}`}
                                                        rows={3}
                                                        onChange={handleChange}
                                                        onFocus={() => setFocused(field.name)}
                                                        onBlur={() => setFocused('')}
                                                        className={getInputClass(field.name)}
                                                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                                                    />
                                                )}

                                                {field.type === 'select' && (
                                                    <div className="select-wrapper">
                                                        <select
                                                            name={field.name}
                                                            value={formData[field.name] || ''}
                                                            required={isRequired}
                                                            onChange={handleChange}
                                                            onFocus={() => setFocused(field.name)}
                                                            onBlur={() => setFocused('')}
                                                            className={getSelectClass(field.name)}
                                                            style={{ fontFamily: "'DM Sans', sans-serif" }}
                                                        >
                                                            <option value="">Select option...</option>
                                                            {field.options?.split(',').map((opt, i) => {
                                                                const val = opt.trim();
                                                                return val ? <option key={i} value={val}>{val}</option> : null;
                                                            })}
                                                        </select>
                                                    </div>
                                                )}

                                                {field.type === 'radio' && (
                                                    <div className="flex flex-wrap gap-2">
                                                        {field.options?.split(',').map((opt, i) => {
                                                            const val = opt.trim();
                                                            if (!val) return null;
                                                            return (
                                                                <div key={i} className="pill-option">
                                                                    <input
                                                                        type="radio"
                                                                        id={`${field.name}-${val}`}
                                                                        name={field.name}
                                                                        value={val}
                                                                        checked={formData[field.name] === val}
                                                                        onChange={handleChange}
                                                                        required={isRequired}
                                                                    />
                                                                    <label htmlFor={`${field.name}-${val}`}>{val}</label>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        <div className="section-divider" />

                        {/* ── Registration & Payment ── */}
                        <p className="sans text-[11px] font-bold tracking-widest uppercase text-[#5928ed]">Registration & Payment / Ticket Configuration</p>

                        {/* Category */}
                        {config?.ticketConfig?.categories?.length > 0 && (
                            <div>
                                <label className="sans block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Registration Category *</label>
                                <div className="space-y-3">
                                    {config.ticketConfig.categories.map((cat) => (
                                        <label
                                            key={cat.id}
                                            htmlFor={`cat-${cat.id}`}
                                            className={`flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-all ${formData.registrationCategory === cat.id ? 'border-[#5928ed] bg-[#fcfaff]' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="font-semibold text-gray-800 text-sm tracking-wide">{cat.name}</span>
                                            </div>
                                            <input
                                                type="radio"
                                                id={`cat-${cat.id}`}
                                                name="registrationCategory"
                                                value={cat.id}
                                                checked={formData.registrationCategory === cat.id}
                                                onChange={handleChange}
                                                className="w-5 h-5 text-[#5928ed] border-[#5928ed] focus:ring-[#5928ed]"
                                            />
                                        </label>
                                    ))}
                                </div>
                                {config.ticketConfig.categories.find(c => c.id === formData.registrationCategory)?.requiresIdNumber && (
                                    <div className="mt-4 fade-in">
                                        <label className="sans block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                            {config.ticketConfig.categories.find(c => c.id === formData.registrationCategory)?.idNumberLabel || 'ID Number'} *
                                        </label>
                                        <input
                                            type="text"
                                            name="categoryIdNumber"
                                            value={formData.categoryIdNumber || ''}
                                            required
                                            placeholder={`Enter ${config.ticketConfig.categories.find(c => c.id === formData.registrationCategory)?.idNumberLabel || 'ID Number'}`}
                                            onChange={handleChange}
                                            onFocus={() => setFocused('categoryIdNumber')}
                                            onBlur={() => setFocused('')}
                                            className={getInputClass('categoryIdNumber')}
                                            style={{ fontFamily: "'DM Sans', sans-serif" }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Fee table */}
                        {formData.registrationCategory && dPrice.activeDr && (
                            <div className="fade-in space-y-4 mt-4">
                                <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                                    <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
                                        <span className="sans text-xs font-bold text-gray-500 uppercase tracking-wider">
                                            {config.ticketConfig.categories.find(c => c.id === formData.registrationCategory)?.name}
                                        </span>
                                        <span className="sans text-xs text-gray-400">Current Applicable Rate</span>
                                    </div>
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-gray-100 bg-gray-50/50">
                                                <th className="sans text-xs text-gray-400 font-semibold px-4 py-2 text-left">Timeline</th>
                                                <th className="sans text-xs text-gray-400 font-semibold px-4 py-2 text-right">Base Conference Fee</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="border-b border-gray-50 transition-colors bg-[#fcfaff]">
                                                <td className="sans text-xs text-gray-700 px-4 py-3">
                                                    {dPrice.activeDr.isSpot ? (
                                                        <span className="inline-flex items-center gap-1.5 font-semibold text-gray-900">
                                                            {dPrice.activeDr.label}
                                                            <span className="bg-purple-100 text-purple-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full">SPOT</span>
                                                        </span>
                                                    ) : (
                                                        <span className="font-semibold text-gray-900">{dPrice.activeDr.label}</span>
                                                    )}
                                                    {dPrice.activeDr.deadline && (
                                                        <span className="block text-[10px] text-gray-500 mt-0.5 font-medium">Valid till: {moment(dPrice.activeDr.deadline).format('DD MMM YYYY')}</span>
                                                    )}
                                                </td>
                                                <td className="sans text-sm font-bold text-right px-4 py-3 text-[#5928ed]">
                                                    {dPrice.isForeign ? `$${dPrice.basePrice}` : `₹${dPrice.basePrice}`}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                {/* Add-ons */}
                                {config?.ticketConfig?.addons?.filter(a => !a.excludedCategories?.includes(formData.registrationCategory)).length > 0 && (
                                    <div className="space-y-3 mt-4">
                                        <label className="sans block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Optional Add-ons</label>
                                        {config.ticketConfig.addons.map(addon => {
                                            if (addon.excludedCategories?.includes(formData.registrationCategory)) return null;

                                            const addonPrice = addon.price ? (addon.isForeign ? `$${addon.price}` : `₹${addon.price}`) : 'Free';

                                            return (
                                                <label key={addon.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-xl cursor-pointer hover:border-[#5928ed] transition-colors">
                                                    <div className="flex items-center gap-3">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!selectedAddons[addon.id]}
                                                            onChange={(e) => handleAddonChange(addon.id, e.target.checked)}
                                                            className="w-4 h-4 text-[#5928ed] border-gray-300 rounded focus:ring-[#5928ed]"
                                                        />
                                                        <span className="text-sm font-medium text-gray-700">{addon.label}</span>
                                                    </div>
                                                    <span className="text-sm font-bold text-gray-600">+{addonPrice}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}

                                {computedPriceStr && (
                                    <div className="fade-in flex items-center gap-3 p-4 rounded-2xl bg-[#fcfaff] border border-[#f0d5c8] mt-6">
                                        <div className="flex-1">
                                            <p className="sans text-xs text-gray-500 font-medium mb-0.5">Your Total Registration Fee</p>
                                            <p className="sans text-xs text-gray-400">Includes Base Price + Add-ons</p>
                                        </div>
                                        <span className="price-badge">🎫 {computedPriceStr}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="section-divider" />

                        {/* ── Document Uploads ── */}
                        <p className="sans text-[11px] font-bold tracking-widest uppercase text-[#5928ed]">Required Documents</p>

                        {(() => {
                            const selectedCat = config?.ticketConfig?.categories?.find(c => c.id === formData.registrationCategory);
                            const requiresHodCert = selectedCat?.requiresHodCert;

                            return (
                                <div className="space-y-5 mt-4">
                                    {/* Photo Upload */}
                                    <div>
                                        <label className="sans block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                            Upload Your Photo *
                                        </label>
                                        <input
                                            ref={photoRef}
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={handlePhotoChange}
                                        />
                                        {photoPreview ? (
                                            <div className="flex items-center gap-4">
                                                <img
                                                    src={photoPreview}
                                                    alt="Your photo"
                                                    className="w-24 h-28 object-cover rounded-2xl border-2 border-[#5928ed] shadow-md"
                                                />
                                                <div className="flex flex-col gap-2">
                                                    <p className="sans text-xs text-gray-500 font-medium">{photoFile?.name}</p>
                                                    <button
                                                        type="button"
                                                        onClick={() => photoRef.current.click()}
                                                        className="sans text-xs text-[#5928ed] font-semibold border-2 border-[#5928ed] rounded-lg px-3 py-1.5 hover:bg-[#fcfaff] transition-colors"
                                                    >
                                                        Change Photo
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                                                        className="sans text-xs text-gray-400 font-medium hover:text-red-400 transition-colors"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div
                                                className="upload-zone p-6 flex flex-col items-center justify-center gap-2 text-center"
                                                onClick={() => photoRef.current.click()}
                                            >
                                                <div className="w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center text-2xl border-2 border-gray-100">
                                                    🧑‍⚕️
                                                </div>
                                                <p className="sans text-sm font-semibold text-gray-600">Click to upload your photo</p>
                                                <p className="sans text-xs text-gray-400">JPG, PNG · Passport size preferred</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Bonafide Certificate — conditionally shown */}
                                    {requiresHodCert && (
                                        <div className="hod-box px-5 py-4 fade-in">
                                            <div className="flex gap-3 mb-3">
                                                <span className="text-xl mt-0.5">📄</span>
                                                <div>
                                                    <p className="sans text-sm font-bold text-blue-800 mb-0.5">Bonafide Certificate Required</p>
                                                    <p className="sans text-xs text-blue-700 leading-relaxed">
                                                        The chosen category requires you to upload a <strong>bonafide certificate signed by your Head of Department / Institution</strong>.
                                                    </p>
                                                </div>
                                            </div>
                                            <input
                                                ref={hodRef}
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                className="hidden"
                                                onChange={handleHodChange}
                                            />
                                            {hodCertFile ? (
                                                <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-blue-200">
                                                    <span className="text-blue-500 text-lg">📎</span>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="sans text-xs font-semibold text-gray-700 truncate">{hodCertFile.name}</p>
                                                        <p className="sans text-xs text-gray-400">Uploaded successfully</p>
                                                    </div>
                                                    <button type="button" onClick={() => hodRef.current.click()}
                                                        className="sans text-xs text-blue-600 font-semibold hover:underline shrink-0">
                                                        Change
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="upload-zone p-4 flex items-center gap-3" onClick={() => hodRef.current.click()}>
                                                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-lg shrink-0">📎</div>
                                                    <div>
                                                        <p className="sans text-sm font-semibold text-gray-600">Upload Bonafide Certificate *</p>
                                                        <p className="sans text-xs text-gray-400">PDF, JPG or PNG accepted</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        <div className="section-divider" />

                        {/* ── Disclaimer & Payment Proof ── */}
                        <p className="sans text-[11px] font-bold tracking-widest uppercase text-[#5928ed]">Payment Information</p>

                        {/* Bank Details Area */}
                        {dPrice.grandTotal > 0 && (
                            <div className="bg-white border-2 border-purple-100 rounded-xl p-5 shadow-sm fade-in space-y-4">
                                <div className="text-center bg-[#fcfaff] border border-[#f0d5c8] rounded-xl p-6 mb-4">
                                    <p className="text-sm text-gray-500 font-bold mb-1 uppercase tracking-wider" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>Your Total Registration Fee</p>
                                    <p className="text-4xl md:text-5xl font-black text-[#5928ed] tracking-tight" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>{dPrice.isForeign ? '$' : '₹'}{dPrice.grandTotal}</p>
                                </div>
                                <p className="sans text-sm font-semibold text-gray-800">Please transfer {dPrice.isForeign ? '$' : '₹'}{dPrice.grandTotal} to the following account:</p>

                                <div className="flex flex-col md:flex-row gap-6 items-center bg-gray-50 p-4 rounded-lg border border-gray-100">
                                    <div className="flex-1 space-y-2 text-sm w-full font-mono text-gray-700">
                                        <div className="flex justify-between border-b border-gray-200 pb-1"><span className="text-gray-500">Account Name:</span><span className="font-bold">JSS Medical College Seminar A/c</span></div>
                                        <div className="flex justify-between border-b border-gray-200 pb-1"><span className="text-gray-500">Account No:</span><span className="font-bold tracking-widest">64143204788</span></div>
                                        <div className="flex justify-between border-b border-gray-200 pb-1"><span className="text-gray-500">IFSC Code:</span><span className="font-bold uppercase">SBIN0040547</span></div>
                                        <div className="flex justify-between border-b border-gray-200 pb-1"><span className="text-gray-500">Bank Name:</span><span className="font-bold">State Bank of India</span></div>
                                        <div className="flex justify-between"><span className="text-gray-500">Branch:</span><span className="font-bold">Sri Shivarathreeshwara Nagara, Mysuru</span></div>
                                    </div>

                                    {/* QR Code */}
                                    <div className="flex flex-col items-center bg-white p-3 rounded-lg border border-gray-200 shadow-sm shrink-0">
                                        <img src="/payment-qr.png" alt="Scan to Pay" className="w-40 h-40 object-contain rounded" />
                                        <span className="sans text-[10px] text-gray-400 mt-2 font-medium uppercase tracking-wider">Scan to Pay</span>
                                    </div>
                                </div>

                                <div className="pt-3 border-t border-purple-100 flex flex-col gap-4">
                                    <div className="w-full">
                                        <label className="sans block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Transaction ID / UTR No. *</label>
                                        <input
                                            type="text"
                                            name="transactionId"
                                            value={formData.transactionId || ''}
                                            onChange={handleChange}
                                            required
                                            className="w-full text-sm py-2 px-3 border border-gray-300 rounded focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                                            placeholder="Ex: UTR1234567890"
                                        />
                                    </div>

                                    <div className="w-full">
                                        <label className="sans block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Upload Payment Screenshot *</label>
                                        <input
                                            ref={transactionReceiptRef}
                                            type="file"
                                            accept="image/*,.pdf"
                                            className="hidden"
                                            onChange={handleTransactionReceiptChange}
                                        />
                                        <div
                                            onClick={() => transactionReceiptRef.current.click()}
                                            className={`p-2 border border-dashed rounded text-center cursor-pointer hover:bg-purple-50 transition-colors ${transactionReceiptFile ? 'bg-purple-50 border-purple-300 border-purple-400' : 'bg-white border-red-300'}`}
                                        >
                                            {transactionReceiptFile ? (
                                                <span className="text-xs font-medium text-purple-700 truncate block px-2">{transactionReceiptFile.name}</span>
                                            ) : (
                                                <span className="text-xs text-gray-500 font-medium flex items-center justify-center gap-1">
                                                    <span className="text-lg">📸</span> Attach Payment Proof (Required)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <p className="sans text-[10px] text-gray-500 italic mt-2 text-center">
                                    Note: Your registration will be reviewed and officially confirmed by the organizing committee upon payment verification.
                                </p>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="pt-2 flex flex-col sm:flex-row gap-3">
                            <button type="submit" disabled={loading}
                                className="submit-btn flex-1 text-white font-semibold py-3.5 px-6 rounded-xl sans text-sm tracking-wide shadow-lg disabled:opacity-75 disabled:cursor-not-allowed">
                                {loading ? 'Processing...' : submitted ? '✓ Submitted Successfully!' : 'Verify & Register'}
                            </button>
                            <button type="button"
                                onClick={() => {
                                    setFormData({
                                        fullName: '', email: '', contactNumber: '', whatsappNumber: '',
                                        organization: '', designation: '', qualification: '', otherQualification: '',
                                        areaOfExpertise: '', otherAreaOfExpertise: '', foodChoice: '',
                                        registrationCategory: '', registrationDate: '', accompanyingSpouse: '',
                                        participantType: '',
                                    });
                                    setPhotoFile(null);
                                    setPhotoPreview(null);
                                    setHodCertFile(null);
                                }}
                                className="sans text-sm font-medium text-gray-400 hover:text-gray-600 py-3.5 px-6 rounded-xl border-2 border-gray-100 hover:border-gray-200 transition-all">
                                Clear form
                            </button>
                        </div>

                    </form>
                </div>

                <p className="sans text-center text-xs text-gray-400 mt-6">© 2026 KAPCON · JSS Medical College, Mysuru</p>
            </div>

            {/* Spouse/Child amenities notice modal */}
            {showSpouseNotice && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="bg-white rounded-2xl shadow-2xl p-7 max-w-sm w-full text-center fade-in">
                        <div className="text-4xl mb-3">ℹ️</div>
                        <h3 className="font-bold text-gray-900 text-lg mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                            Please Note
                        </h3>
                        <p className="sans text-sm text-gray-600 leading-relaxed mb-6">
                            Amenities for Accompanying Spouse / Child <strong>exclude Banquet and Delegate Kit</strong>.
                        </p>
                        <button
                            onClick={() => setShowSpouseNotice(false)}
                            className="submit-btn w-full text-white font-semibold py-3 px-6 rounded-xl sans text-sm tracking-wide"
                        >
                            Understood
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomEventRegistration;